import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { ProductAction, SiteAction } from '../../actions';
import { UserType } from '../../config';
import { ORG_HQ, USER_TYPE_DEALER } from '../../config/appConstants';
import { Translate as t } from '../../config/Translate';
import { RayForm } from '../common';
import { EditSaveCancelBar } from '../common/Buttons';

// 칸별 편집 권한의 단일 정의. ProductInfo 의 칸 isEditable 과 ProductView 의 연필 노출이 함께 쓴다.
//   modelOption: 모델/옵션 (HQ 의 지정 계정만), adminFields: 설치일·출하일·Operator (슈퍼관리자).
const getProductFieldPermission = (user, userType) => ({
	modelOption: !!user && userType == ORG_HQ && user?.email == "hyunseok.jeong@raymedical.co.kr",
	adminFields: user?.adminType == 'S',
});

// 사이트 칸은 관리자 포털에서만 편집 대상이다(Tech 는 getControl 에서 제외).
export const hasEditableProductField = (user, isAdminPortal = false) => {
	if (isAdminPortal) return true;
	const userType = UserType(user?.companyId, user?.companyInfo?.org);
	const perm = getProductFieldPermission(user, userType);
	return perm.modelOption || perm.adminFields;
};

const ProductInfo = ({ user, productInfo, UpdateProductInfo, clist, editable, showsn = false, _sn, companies, cancelcallback = null, nocard = false }) => {
	const isAdmin = location.pathname.toLowerCase().startsWith('/admin/');

	const [product, setProduct] = useState(productInfo);
	const [sites, setSites] = useState([]);
	const [userType, setUserType] = useState('DEALER');
	const [warranty, setWarranty] = useState(0);
	const [branchWarranty, setBranchWarranty] = useState(0);
	const [saving, setSaving] = useState(false);

	const { model, option } = useMemo(() => {
		const productOptions = clist?.filter(item => item.type === 'PRODUCTOPTION') || [];
		const models = [];
		const options = [];

		productOptions.forEach(item => {
			if (!models.some(m => m.code === item.group))
				models.push({ code: item.group, title: item.group });

			options.push({ group: item.group, code: item.code, title: item.name });
		});

		models.push({ code: 'RAYSCAN S', title: 'RAYSCAN S' });

		return { model: models, option: options };
	}, [clist]);

	// 사이트 목록은 **caller 스코프**다 — 장비의 조직과 무관하다.
	//   docs/specs/site-domain-model.md §2.1/§2.3. 종전에는 목록을 채우는 함수가
	//   호출조차 되지 않아(죽은 코드) 현재 값 1개만 남았고, 이후 장비 회사로 좁혔던 것도
	//   도메인 오해였다. 회사와 무관하므로 **마운트 시 한 번만** 불러온다.
	useEffect(() => {
		let alive = true;

		SiteAction.GetSiteCombo().then((data) => {
			if (!alive) return;
			setSites(Array.isArray(data) ? data : []);
		});

		return () => { alive = false; };
	}, []);

	const perm = getProductFieldPermission(user, userType);

	useEffect(() => {
		setUserType(UserType(user.companyId, user?.companyInfo?.org));
	}, [user]);

	// 입력 state 를 마지막 서버 값(productInfo)으로 덮는 단일 함수 — 초기 동기화, [취소], 편집 닫힘이 같이 쓴다.
	const syncProductFromServer = () => setProduct(productInfo);

	// 헤더 잠금/연필로 편집을 닫을 때도 [취소]와 같이 서버 값으로 되돌린다(저장 성공 시에는 이미 갱신된 productInfo 로 복원).
	useEffect(() => {
		if (!editable) syncProductFromServer();
	}, [editable]);

	useEffect(() => {
		syncProductFromServer();

		let msPeriod = product.waranties?.find(x => x.category === 'wartype_ms' && x.dest === "warproduct_product")?.period || 0;

		if (msPeriod > 0) {
			productInfo.exWarranty?.filter(x => x.type === 'HQ' && !x.isDel).forEach(x => {
				if (x.dest.includes('warproduct_product'))
					msPeriod = moment(msPeriod).add(x.amount, x.amountType);
			});
		}

		setWarranty(msPeriod);

		let bsPeriod = product.waranties?.find(x => x.category === 'wartype_bs' && x.dest === "warproduct_product")?.period || 0;

		if (bsPeriod > 0) {
			productInfo.exWarranty?.filter(x => x.type === 'BRANCH' && !x.isDel).forEach(x => {
				if (x.dest.includes('warproduct_product'))
				bsPeriod = moment(bsPeriod).add(x.amount, x.amountType);
			});
		}

		setBranchWarranty(bsPeriod);

	}, [productInfo, userType]);

	// 현재 연결된 site 가 목록(회사 범위) 밖이면 선택지로 주입한다. 목록에 없으면
	// react-select 가 선택 값을 표시하지 못해 "설정돼 있는데 비어 보이는" 상태가 된다.
	//
	// 이름 우선순위 — `product.site.sitename` 이 먼저다(리뷰 지적).
	//   단건 조회(getProductDoc)는 `baseFilter` 로 sites 를 **실제 $lookup** 하므로
	//   `product.site.sitename` 은 sites 컬렉션의 현재 값이고, `product.sitename` 은
	//   비거나 낡을 수 있는 denormalized 사본이다(그 사본이 비는 것이 BUG-214 자체다).
	//   둘 다 없으면 N/A — 원시 siteId 를 라벨로 쓰지 않는다(다른 항목은 병원 이름인데
	//   현재 값만 ObjectId 로 보이게 된다).
	const siteListWithCurrent = (() => {
		const rows = Array.isArray(sites) ? sites : [];
		if (!product.siteId) return rows;
		if (rows.some((x) => x?.code === product.siteId)) return rows;
		return [{ code: product.siteId, title: product.site?.sitename || product.sitename || t('common.label.na') }, ...rows];
	})();

	const onChanges = (e) => {
		if (e.target.name == 'model') {
			const options = option.filter(x => x.group == (e.target.value === 'RAYSCAN S' ? 'RAYSCAN Studio' : e.target.value));
			setProduct({ ...product, model: e.target.value, option: options[0].title, codes: options[0].code });
		}
		else if (e.target.name == 'installedDate' || e.target.name == 'shipmentdate' || e.target.name == 'siteId') {
			let updateInfo = { ...product, [e.target.name]: e.target.value };

			if (e.target.name == 'siteId')
				updateInfo = { ...updateInfo, 'sitename': sites.find(x => x.code == e.target.value)?.title || '' };

			setProduct(updateInfo);
		}
		else if (e.target.name === 'companyId') {
			// §7.5: 회사(Operator)가 바뀌어도 **설치 위치는 지우지 않는다.**
			//   장비 소유 조직이 바뀌어도 장비는 그 병원에 그대로 있다.
			setProduct({ ...product, [e.target.name]: e.target.value });
		}
		else {
			setProduct({ ...product, option: e.target[e.target.selectedIndex].text, codes: e.target.value });
		}
	}

	const updateProductInfo = async () => {
		setSaving(true);
		try {
			let codes;

			if (product.model && product.option && !product.codes) {
				const result = option.filter(x => { return x.group == product.model && x.title == product.option });
				codes = result.length && result[0].code;
			} else {
				codes = product.codes;
			}

			const ok = await UpdateProductInfo(product._id, {
				sn: product.sn,
				model: product.model,
				option: product.option,
				codes,
				installedDate: product.installedDate,
				shipmentdate: product.shipmentdate,
				companyId: product.companyId,
				siteId: product.siteId,
				sitename: product?.sitename
			});
			// 편집을 닫으면 입력이 서버 값으로 되돌아가므로 저장 실패 때는 열어 둔다.
			if (ok === true) cancelcallback && cancelcallback();
		} finally {
			setSaving(false);
		}
	}

	const form_controls = [
		{
			title: '',
			items: [
				{
					type: 'input',
					name: 'sn',
					label: t('product.field.sn'),
					val: product.sn,
					extra: product,
					viewLinkPath: isAdmin ? "/Admin/AdminProductInfo/" : "/Product/",
					isEditable: false,
					required: true
				},
				{
					type: 'select',
					name: 'model',
					label: t('product.field.model'),
					val: product.model,
					list: model.filter(x => user?.isMegagen ? x.code == 'R2 Studio Q' : x.code == x.code),
					required: true,
					isEditable: !!editable && perm.modelOption,
					onchange: onChanges
				},
				{
					type: 'select',
					name: 'option',
					label: t('common.label.option'),
					val: product.codes,
					list: option.filter(x => x.group == (product.model === 'RAYSCAN S' ? 'RAYSCAN Studio' : product.model)),
					required: true,
					isEditable: !!editable && perm.modelOption,
					onchange: onChanges
				},
				{
					type: 'datetime',
					name: 'installedDate',
					label: t('installation.field.installedDate'),
					val: product.installedDate || 0,
					valtype: 'date',
					onlyDate: true,
					isEditable: perm.adminFields && editable,
					onchange: onChanges
				},
				{
					type: 'datetime',
					name: 'shipmentdate',
					label: t('product.field.shipmentDate'),
					val: product.shipmentdate || 0,
					valtype: 'date',
					onlyDate: true,
					isEditable: perm.adminFields && editable,
					onchange: onChanges
				},
				{
					type: 'input',
					name: 'lastConnectedDate',
					label: t('product.label.lastConnected'),
					val: product.lastConnectedDate,
					valtype: 'date'
				},
				{
					type: 'input',
					name: 'registered',
					label: t('common.status.registered'),
					val: product.registered,
					valtype: 'date',
					onlyDate: true
				},
				{
					type: 'input',
					name: 'warrantyperiod',
					label: t('warranty.title'),
					val: userType === USER_TYPE_DEALER ? 0 : warranty,
					valtype: 'date',
					isEditable: false,
					onlyDate: true,
				},
				{
					type: 'input',
					name: 'branchwarrantyperiod',
					label: t(userType === USER_TYPE_DEALER ? 'warranty' : 'branchwarranty'),
					val: branchWarranty,
					valtype: 'date',
					isEditable: false,
					onlyDate: true
				},
				{
					type: 'select2',
					name: 'companyId',
					label: t('user.type.operator'),
					val: product.companyId,
					list: companies.map(x => ({ code: x.companyId, title: x.name }))
						.sort((a, b) => (a.title || '').localeCompare(b.title || '')),
					isEditable: perm.adminFields && editable,
					onchange: onChanges
				},
				{
					type: 'select2search',
					name: 'siteId',
					label: t('site.title'),
					val: product.siteId,
					// §7.2: 사이트(설치 위치) 변경은 **장비가 소속된 조직의 사용자 전원**에게
					//   허용된다. adminType 'S' 는 회사 이관(위 companyId)에만 적용한다.
					// 현재 연결된 site 가 목록 밖일 수 있다(소속 없는 site 등). 그대로 두면
					// 선택 값이 표시되지 않으므로 선택지로 주입한다. 이름이 없으면 N/A —
					// 원시 siteId(ObjectId)를 라벨로 노출하지 않는다.
					list: siteListWithCurrent,
					isEditable: editable,
					extra: product.site,
					viewLinkPath: isAdmin ? "/Admin/AdminSiteInfo/" : "/Site/",
					viewLinkUseId: product.siteId,
					onchange: onChanges
				}
			]
		},
	];

	useEffect(() => {
		makeExtraElements();
	}, []);

	const makeExtraElements = () => {
		if (product.system) {
			let obj = { title: t('product.label.systemInformation'), items: [] };
			const keyList = Object.keys(product.system);
			for (let i in keyList) {
				const key = keyList[i];
				obj.items.push({
					type: 'input',
					name: key,
					label: key.charAt(0).toUpperCase() + key.slice(1),
					val: product.system[key],
				});
				i++;
				if (i % 2 == 0) {
					form_controls.push(obj);
					obj = { title: '', items: [] };
				}
			}
			obj.items.length > 0 && form_controls.push(obj);
		}
	};

	const getControl = () => {
		if (!editable && !showsn) {
			return [{ title: "", items: form_controls[0].items.filter(x => x.name != "sn") }];
		}
		else if (!location.pathname.toLowerCase().startsWith('/admin/')) {
			return [{ title: "", items: form_controls[0].items.filter(x => x.name != "siteId") }];
		}
		else {
			return form_controls;
		}
	}

	return (
		<>
			<RayForm controls={getControl()} handleChangeData={onChanges} nocard={nocard} />
			{editable &&
				<div className="ps-1 pe-1">
					{/* 편집 진입은 헤더 잠금/연필이 맡는다(다른 탭·요소도 같은 editable 로 제어) — 여기는 열린 상태의 [저장]·[취소]. */}
					<EditSaveCancelBar editing onSave={() => updateProductInfo()} onCancel={() => { syncProductFromServer(); cancelcallback && cancelcallback(); }} saving={saving} saveLabelKey="common.button.update" />
				</div>
			}
		</>
	)
};

const mapState = (state) => {
	const user = state.AuthReducer.user;
	const prd = state.ProductReducer.product;
	const clist = state.RayCodeReducer.items;
	const companies = state.CompanyReducer.companies;
	return { user, prd, clist, companies };
};

const mapDispatch = (dispatch) => ({
	UpdateProductInfo: (_id, data) => dispatch(ProductAction.UpdateProductInfo(_id, data)),
});

export default connect(mapState, mapDispatch)(ProductInfo);
